import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Download } from 'lucide-react';
import DataTable, { Column } from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import { useTranslation } from 'react-i18next';
import { fetchAnalysisResults } from '../lib/api';
import { useTenant } from '../contexts/TenantContext';
import { useAutoRefresh } from '../contexts/AutoRefreshContext';
import { exportToCSV } from '../lib/export';
import { format } from 'date-fns';
import { toast } from 'sonner';

interface AnalysisResult {
  id: number;
  analyzer: string;
  tenant_id: string;
  status: string;
  summary: string;
  score?: number;
  created_at: string;
}

interface PaginatedResponse {
  items: AnalysisResult[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export default function AnalysisPage() {
  const [data, setData] = useState<PaginatedResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [analyzer, setAnalyzer] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const { selectedTenantId } = useTenant();
  const { subscribe } = useAutoRefresh();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const filteredItems = useMemo(() => {
    if (!search || !data?.items) return data?.items || [];
    const q = search.toLowerCase();
    return data.items.filter(
      (r) =>
        r.summary.toLowerCase().includes(q) ||
        r.analyzer.toLowerCase().includes(q) ||
        r.tenant_id.toLowerCase().includes(q)
    );
  }, [data?.items, search]);

  const handleExport = () => {
    if (!filteredItems.length) return;
    exportToCSV(
      filteredItems.map((r) => ({
        ...r,
        created_at: r.created_at ? format(new Date(r.created_at), 'yyyy-MM-dd HH:mm') : '',
        score: r.score != null ? r.score.toFixed(1) : '',
      })),
      [
        { key: 'id', header: 'ID' },
        { key: 'analyzer', header: 'Analyzer' },
        { key: 'tenant_id', header: 'Tenant' },
        { key: 'status', header: 'Status' },
        { key: 'summary', header: 'Summary' },
        { key: 'score', header: 'Score' },
        { key: 'created_at', header: 'Date' },
      ],
      `analysis-results-${format(new Date(), 'yyyy-MM-dd')}`
    );
    toast.success(t('common.exported', { count: filteredItems.length }));
  };

  const loadData = useCallback(() => {
    setLoading(true);
    fetchAnalysisResults({
      page,
      page_size: 20,
      analyzer: analyzer || undefined,
      tenant_id: selectedTenantId || undefined,
      status: status || undefined,
    })
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [page, analyzer, status, selectedTenantId]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => subscribe(loadData), [subscribe, loadData]);

  const columns: Column<AnalysisResult>[] = [
    {
      key: 'analyzer',
      header: t('analysis.analyzer'),
      render: (r) => <span className="capitalize">{r.analyzer.replace(/_/g, ' ')}</span>,
    },
    { key: 'tenant_id', header: t('analysis.tenant') },
    {
      key: 'status',
      header: t('analysis.status'),
      render: (r) => <StatusBadge status={r.status} />,
    },
    { key: 'summary', header: t('analysis.summary'), render: (r) => <span className="truncate max-w-xs block">{r.summary}</span> },
    {
      key: 'score',
      header: t('analysis.score'),
      render: (r) => (r.score != null ? `${r.score.toFixed(1)}%` : '-'),
    },
    {
      key: 'created_at',
      header: t('analysis.date'),
      render: (r) => r.created_at ? format(new Date(r.created_at), 'MMM d, HH:mm') : '-',
    },
  ];

  const analyzers = [
    'compliance', 'agent_health', 'alert_aggregation', 'drift_detection',
    'software_inventory', 'network_audit', 'patch_monitor', 'hardening_score', 'cert_expiry',
  ];

  const selectClass = "text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200";

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('common.search')}
            className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-48"
          />
        </div>
        <select
          value={analyzer}
          onChange={(e) => { setAnalyzer(e.target.value); setPage(1); }}
          className={selectClass}
        >
          <option value="">{t('analysis.allAnalyzers')}</option>
          {analyzers.map((a) => (
            <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          className={selectClass}
        >
          <option value="">{t('analysis.allStatus')}</option>
          <option value="ok">OK</option>
          <option value="warning">Warning</option>
          <option value="critical">Critical</option>
        </select>
        <button
          onClick={handleExport}
          disabled={!filteredItems.length}
          className="flex items-center gap-1.5 text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          <Download className="w-4 h-4" /> CSV
        </button>
        {data && (
          <p className="text-sm text-gray-500 dark:text-gray-400 ml-auto">
            {filteredItems.length}/{data.total} results &middot; Page {data.page}/{data.total_pages}
          </p>
        )}
      </div>

      {loading ? (
        <div className="animate-pulse bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 h-96" />
      ) : (
        <>
          <DataTable
            data={filteredItems}
            columns={columns}
            keyExtractor={(r) => r.id}
            onRowClick={(r) => navigate(`/analysis/${r.id}`)}
            emptyMessage={t('analysis.noResults')}
          />
          {data && data.total_pages > 1 && (
            <div className="flex justify-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200"
              >
                {t('common.previous')}
              </button>
              <button
                onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))}
                disabled={page >= data.total_pages}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200"
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
